import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DesktopDateTimePicker } from '@mui/x-date-pickers';
import { CalendarOrClockPickerView } from '@mui/x-date-pickers/internals/models';
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar';
import dayjs from 'dayjs';
import React, { ElementType } from 'react';
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import { DateFieldConfiguration } from '../../../api/ConfigApi';
import { AuthData } from '../../../context/auth';
import FieldLabel from '../../fields/FieldLabel';
import {
	DATE_PRECISION,
	DATE_PRECISION_INPUT_FORMAT,
	DATE_PRECISION_PLACEHOLDER,
	DATE_PRECISION_VIEWS,
} from '../data/constants';
import { dateConversion } from '../utils/dateConversion';

type FieldProps = Omit<DateFieldConfiguration, 'name'>;
interface Props<T> extends FieldProps, UseControllerProps<T> {
	authData: AuthData;
	disableTimePicker?: boolean;
	defaultToMidnight?: boolean;
	twentyFourHour?: boolean;
	CustomActionBar?: ElementType<PickersActionBarProps>;
	actionBarProps?: any;
	endAdornment?: React.ReactNode;
	defaultCalendarMonth?: dayjs.Dayjs;
	helperText?: string;
	variant?: 'outlined' | 'standard' | 'filled';
	save?: (currValue: string, fieldName: string) => void;
}

const DateField = <T extends FieldValues>(props: Props<T>) => {
	const {
		control,
		name,
		title,
		optional,
		disabled,
		showCalendar,
		precision,
		save,
		authData,
		disableTimePicker,
		CustomActionBar,
		actionBarProps,
		defaultCalendarMonth,
		helperText,
		defaultToMidnight,
		twentyFourHour,
		variant = 'standard',
	} = props;
	const timezone = authData?.auth?.timezone;
	const { maxDate, minDate } =
		dateConversion.getDateBoundaryForDateField(props);
	const endAdornment = (
		<InputAdornment position='end'>
			<Typography sx={{ color: disabled ? 'text.disabled' : 'inherit' }}>
				{dateConversion.getZoneNameAbbreviation(timezone)}
			</Typography>
		</InputAdornment>
	);

	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState: { error } }) => {
				let views = [] as CalendarOrClockPickerView[];
				let openTo = 'day' as CalendarOrClockPickerView;
				const dateTimePrecisionValues = DATE_PRECISION_VIEWS[precision];

				if (
					precision === DATE_PRECISION.YEAR ||
					precision === DATE_PRECISION.MONTH
				) {
					views = [...dateTimePrecisionValues];
					openTo = 'year';
				} else if (precision === DATE_PRECISION.DAY) {
					views = [...dateTimePrecisionValues];
					openTo = 'day';
				} else if (
					precision === DATE_PRECISION.HOUR ||
					precision === DATE_PRECISION.MINUTE
				) {
					views = !disableTimePicker
						? [...dateTimePrecisionValues]
						: DATE_PRECISION_VIEWS[DATE_PRECISION.DAY]; // ['year', 'month', 'day']
					openTo = 'day';
				} else if (precision === DATE_PRECISION.SECOND) {
					// as to not affect FEv2 date fields set to "seconds" precision, we will only show the hour picker for now
					views = !disableTimePicker
						? DATE_PRECISION_VIEWS[DATE_PRECISION.HOUR] // ['year', 'month', 'day', 'hours']
						: DATE_PRECISION_VIEWS[DATE_PRECISION.DAY];
					openTo = 'day';
				}

				let value = !error
					? dateConversion.dateFormStringToDayJs(field.value, precision)
					: field.value;

				const changeYear = (year: dayjs.Dayjs) => {
					// if precision is seconds and year is changed via picker, set date to midnight
					if (precision === DATE_PRECISION.SECOND) {
						const newDate = year
							.set('hour', 0)
							.set('minute', 0)
							.set('second', 0);
						const formattedDate = dateConversion.dayJsObjectToDateFormString(
							newDate,
							precision,
						);
						field.onChange(formattedDate);
					}
				};
				return (
					<DesktopDateTimePicker
						value={value}
						openTo={openTo}
						onYearChange={changeYear}
						onChange={(newValue) => {
							field.onBlur();
							let newDateTimeValue = newValue;
							if (
								defaultToMidnight &&
								newValue?.isValid() &&
								(precision === DATE_PRECISION.HOUR ||
									precision === DATE_PRECISION.MINUTE ||
									precision === DATE_PRECISION.SECOND)
							) {
								// when default to midnight is true, set the time to midnight if the date is changed (hour, minute, second precision)
								const dayDiff =
									!error && !isNaN(newValue?.diff(value, 'day'))
										? newValue?.diff(value, 'day')
										: 0;
								newDateTimeValue =
									dayDiff !== 0
										? newValue.set('hour', 0).set('minute', 0).set('second', 0)
										: newValue;
							}

							let dateString = dateConversion.dayJsObjectToDateFormString(
								newDateTimeValue,
								precision,
							);
							field.onChange(dateString);
							if (newDateTimeValue?.isValid()) {
								save &&
									save(
										dateConversion.dayJsToLocalizedBackendFormattedString(
											newDateTimeValue,
											precision,
											timezone,
										),
										name,
									);
							}
						}}
						components={{
							ActionBar: CustomActionBar,
						}}
						componentsProps={{
							actionBar: actionBarProps,
						}}
						views={views}
						inputFormat={DATE_PRECISION_INPUT_FORMAT[precision]}
						minDateTime={minDate}
						maxDateTime={maxDate}
						disabled={disabled}
						defaultCalendarMonth={defaultCalendarMonth}
						ampm={twentyFourHour ? false : true}
						renderInput={(params) => (
							<FormControl
								sx={{
									width: '100%',
								}}
							>
								<FieldLabel title={title} required={!optional} htmlFor={name} />
								<TextField
									{...params}
									id={name}
									fullWidth
									error={!!error}
									variant={variant}
									inputProps={{
										...params.inputProps,
										placeholder:
											DATE_PRECISION_PLACEHOLDER[precision] ||
											DATE_PRECISION_PLACEHOLDER[DATE_PRECISION.DAY],
									}}
									InputProps={{
										endAdornment: (
											<>
												{(precision === DATE_PRECISION.HOUR ||
													precision === DATE_PRECISION.MINUTE ||
													precision === DATE_PRECISION.SECOND) &&
													endAdornment}
												{showCalendar && params.InputProps?.endAdornment}
											</>
										),
									}}
									helperText={
										error?.message
											? error.message
											: helperText ||
											  `Format: ${DATE_PRECISION_PLACEHOLDER[precision]}`
									}
								/>
							</FormControl>
						)}
					/>
				);
			}}
		/>
	);
};

export default DateField;
